import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CalendarClock, Link2, MapPin } from "lucide-react";
import {
    useSchedulerSettings,
    useUpcomingSessions,
} from "../../application/useScheduler";
import { formatBookingTime, isInSameWeek, toDateKey } from "../../domain/formatting";
import type { UpcomingSubjectFilter } from "../../domain/types";

interface Props {
    /** Narrows to one patient or couple. Absent means the whole caseload. */
    subject?: UpcomingSubjectFilter | null;
    /** Falls back to this when the subject's name is not otherwise known. */
    subjectLabel?: string | null;
    /**
     * Restrict to the current Monday-to-Sunday week.
     *
     * The home page wants this: a session three weeks out is not something a
     * therapist needs on their landing page, and showing it would make an
     * empty week look full. A patient page wants the opposite — "when do I
     * next see them" has no week boundary.
     */
    weekOnly?: boolean;
    className?: string;
}

/** The next scheduled session, for a subject or across the whole caseload. */
export default function NextSessionCard({
    subject = null,
    subjectLabel = null,
    weekOnly = false,
    className = "rounded-xl border border-border-subtle bg-surface-raised p-5",
}: Props) {
    const { t } = useTranslation();
    const settingsQuery = useSchedulerSettings();
    // Null until known. Never guess a zone: labelling a local time with the
    // wrong one states something false, even briefly.
    const timezone = settingsQuery.data?.timezone ?? null;
    const upcoming = useUpcomingSessions(subject);

    const sessions = useMemo(() => upcoming.data ?? [], [upcoming.data]);

    const next = useMemo(() => {
        // The list arrives earliest-first, so the first match is the answer.
        if (!weekOnly) return sessions[0] ?? null;
        const today = toDateKey(new Date(), timezone ?? "UTC");
        return (
            sessions.find((session) =>
                isInSameWeek(session.date.slice(0, 10), today)
            ) ?? null
        );
    }, [sessions, weekOnly, timezone]);

    // An empty week reads as an empty schedule unless the next one is named.
    const beyondThisWeek = weekOnly && !next ? (sessions[0] ?? null) : null;

    // The session carries its own subject name, so nothing here needs the
    // therapist's whole caseload loaded to render one label.
    const name = next?.subject_label ?? subjectLabel ?? undefined;

    return (
        <div className={className}>
            <div className="mb-3 flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-accent-text" />
                <h3 className="text-sm font-semibold text-content-primary">
                    {t(
                        weekOnly
                            ? "scheduler.nextSession.title"
                            : "scheduler.nextSession.titleForSubject"
                    )}
                </h3>
            </div>

            {upcoming.isLoading ? (
                <p className="text-sm text-content-tertiary">
                    {t("scheduler.nextSession.loading")}
                </p>
            ) : upcoming.isError ? (
                <div className="flex flex-col items-start gap-2">
                    <p role="alert" className="text-sm text-red-600">
                        {t("scheduler.nextSession.error")}
                    </p>
                    <button
                        type="button"
                        onClick={() => upcoming.refetch()}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm dark:border-gray-700"
                    >
                        {t("scheduler.nextSession.retry")}
                    </button>
                </div>
            ) : !next ? (
                <div className="flex flex-col items-start gap-2">
                    <p className="text-sm text-content-tertiary">
                        {t(
                            weekOnly
                                ? "scheduler.nextSession.emptyThisWeek"
                                : "scheduler.nextSession.emptyForSubject"
                        )}
                    </p>
                    {beyondThisWeek && (
                        <p className="text-sm text-content-tertiary">
                            {t("scheduler.nextSession.nextBeyondWeek", {
                                date: beyondThisWeek.date.slice(0, 10),
                            })}
                        </p>
                    )}
                    <Link
                        to="/scheduler"
                        className="text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
                    >
                        {t("scheduler.nextSession.openScheduler")}
                    </Link>
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    <p className="text-base font-semibold text-content-primary">
                        {name ?? t("scheduler.nextSession.unknownSubject")}
                    </p>
                    <p className="text-sm text-content-tertiary">
                        {`${next.date.slice(0, 10)} · ${formatBookingTime(next.date.slice(0, 10), next.hour, timezone)} · ${next.duration} min`}
                    </p>
                    {next.meeting_link ? (
                        // The one actionable thing on the card, so it is a
                        // button rather than a line of grey text among the
                        // logistics.
                        <a
                            href={next.meeting_link}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex w-fit items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
                        >
                            <Link2 className="h-4 w-4" />
                            {t("scheduler.nextSession.joinLink")}
                        </a>
                    ) : (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-content-tertiary">
                            <MapPin className="h-3.5 w-3.5" />
                            {t("scheduler.nextSession.inPerson")}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
